import { useMutation, useQuery } from "@apollo/client/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Select } from "@/components/design/Select";
import { OrganizationalRecordPerson } from "./OrganizationalRecordPerson";
import { CommentArea, type DiscussionThread, type DiscussionComment } from "@/components/common/CommentArea";
import { DeleteOrganizationalRecordCommentDocument, MyselfDocument, OrganizationalRecordCommentsDocument,
  OrganizationalRecordLinkedCommentDocument, SortOrder } from "@/graphql/generated/operations";
import { OrganizationalRecordLinkEditor, OrganizationalRecordLinkList, type OrganizationalRecordNavigate } from "./OrganizationalRecordLinks";
import { useOrganizationalRecordDrafts, commentDraftKey, isOrganizationalRecordDraftDirty, type OrganizationalRecordDraftValue } from "./OrganizationalRecordDrafts";

type CommentsProps = {
  recordId: string; entryId?: string; canWrite: boolean; onNavigate?: OrganizationalRecordNavigate; focusId?: string; embedded?: boolean;
};

export function OrganizationalRecordComments(props: CommentsProps) {
  const intl = useIntl();
  const [sortOrder, setSortOrder] = useState(SortOrder.Desc);
  return <div className="w-full max-w-[48rem] space-y-4">
    <div className="flex justify-end">
      <label className="flex items-center gap-3">
        <span className="smycles-field-meta text-ink-muted"><FormattedMessage id="sort.label" /></span>
        <Select variant="plain" hideEmpty value={sortOrder} onChange={value => setSortOrder(value as SortOrder)} options={[
          { value: SortOrder.Desc, label: intl.formatMessage({ id: "organizationalRecord.comments.newest" }) },
          { value: SortOrder.Asc, label: intl.formatMessage({ id: "organizationalRecord.comments.oldest" }) },
        ]} />
      </label>
    </div>
    <CommentThread key={sortOrder} {...props} sortOrder={sortOrder} />
  </div>;
}

function CommentThread({ recordId, entryId, canWrite, onNavigate, focusId, embedded = false, sortOrder }: CommentsProps & { sortOrder: SortOrder }) {
  const input = { recordId, entryId: entryId ?? null, first: 50 };
  const query = useQuery(OrganizationalRecordCommentsDocument, { variables: { input, sortOrder }, fetchPolicy: "no-cache" });
  const focused = useQuery(OrganizationalRecordLinkedCommentDocument, { variables: { id: focusId ?? "" }, skip: !focusId, fetchPolicy: "no-cache" });
  const store = useOrganizationalRecordDrafts();
  const comments = query.error ? [] : query.data?.organizationalRecordComments.nodes ?? [];
  const focus = focused.data?.organizationalRecordComment;
  const visible = focus && focus.recordId === recordId && (focus.entryId ?? null) === (entryId ?? null)
    ? [focus, ...comments.filter(comment => comment.id !== focus.id)] : comments;
  const { data: me } = useQuery(MyselfDocument);
  const [remove, deleting] = useMutation(DeleteOrganizationalRecordCommentDocument, { fetchPolicy: "no-cache" });
  const initial = (comment: DiscussionComment | null): OrganizationalRecordDraftValue => ({ type: "comment", id: comment?.id, entryId,
    content: comment?.content ?? "", links: comment ? visible.find(item => item.id === comment.id)?.links ?? [] : [] });
  const key = (comment: DiscussionComment | null) => commentDraftKey(entryId, comment?.id);
  const edits = Object.fromEntries(visible.flatMap(comment => {
    const draft = store.drafts[key(comment)];
    return draft ? [[comment.id, { content: draft.content, dirty: isOrganizationalRecordDraftDirty(draft) }]] : [];
  }));
  const thread: DiscussionThread = {
    comments: visible, loading: query.loading || focused.loading, busy: deleting.loading || store.busy,
    error: query.error ?? focused.error ?? deleting.error ?? null,
    hasNextPage: query.data?.organizationalRecordComments.pageInfo.hasNextPage,
    loadMore: () => query.fetchMore({
      variables: { input: { ...input, after: query.data?.organizationalRecordComments.pageInfo.endCursor }, sortOrder },
      updateQuery: (previous, { fetchMoreResult: next }) => ({ ...next, organizationalRecordComments: { ...next.organizationalRecordComments,
        nodes: [...previous.organizationalRecordComments.nodes, ...next.organizationalRecordComments.nodes] } }),
    }),
    async remove(id) {
      await remove({ variables: { id } }); store.discard(commentDraftKey(entryId, id));
      await query.refetch(); if (focusId === id) await focused.refetch();
    },
  };
  return <CommentArea thread={thread} meId={me?.myself?.id ?? null} canWrite={canWrite} embedded={embedded}
    renderAuthor={comment => <OrganizationalRecordPerson person={comment.person} />}
    drafts={{ text: store.drafts[key(null)]?.content ?? "", onTextChange: content => store.update(key(null), initial(null), { content }), edits,
      onEdit: comment => store.update(key(comment), initial(comment)),
      onEditChange: (comment, content) => store.update(key(comment), initial(comment), { content }),
      onDiscard: comment => store.discard(key(comment)),
    }}
    renderComposer={(comment, disabled) => <OrganizationalRecordLinkEditor recordId={recordId} initial={initial(comment).links}
      value={store.drafts[key(comment)]?.links ?? initial(comment).links} disabled={disabled}
      onChange={links => store.update(key(comment), initial(comment), { links })} />}
    renderCommentExtras={comment => {
      const links = store.drafts[key(comment)]?.links ?? initial(comment).links;
      return links.length ? <OrganizationalRecordLinkList links={links} onNavigate={onNavigate} /> : null;
    }}
  />;
}
